import type {
  Absence,
  Appointment,
  ScheduleEntry,
  ScheduleResult,
  TaskListItem,
} from "@myapp/contract";

/**
 * The calendar's view model.
 *
 * `GET /schedule` returns one flat, pre-sorted `entries` timeline per technician plus deduped
 * `appointments` / `tasks` / `absences` lookup arrays. The calendar widgets, however, render one
 * component per kind and want them already split apart. This adapter does that split once, near
 * the data, so every block component keeps the props it already had.
 *
 * `technicianId` stays a string here because that is what the widgets already pass around; the
 * server speaks numbers. The conversion lives in this one function rather than in the four
 * components that would otherwise each need touching.
 */
export interface TechnicianScheduleView {
  technicianId: string;
  technicianName: string | null;
  technicianAvatar: string | null;
  workingDays: number[];
  shiftStart: string | null;
  shiftEnd: string | null;
  defaultBreakStart: string | null;
  breakDurationMinutes: number;
  appointments: Appointment[];
  /**
   * Every BREAK entry for this technician — materialized *and* projected. A projected one has
   * `id: null`, which is exactly what `resolveBreakForDay` keys "can this be dragged" on.
   */
  breakOverrides: ScheduleEntry[];
  /** ABSENCE entries. One per technician, so a 3-technician absence appears in three views. */
  timeManagementEntries: ScheduleEntry[];
  scheduledTasks: TaskListItem[];
  absences: Absence[];
}

export function selectTechnicianSchedule(
  result: ScheduleResult | undefined,
  technicianId: string | number | null | undefined,
): TechnicianScheduleView | null {
  if (!result || technicianId == null) return null;

  const wanted = Number(technicianId);
  const technician = result.technicians.find((t) => t.technicianId === wanted);
  if (!technician) return null;

  return toView(result, technician);
}

/** Every technician's view, for the all-technicians board. */
export function selectAllTechnicianSchedules(
  result: ScheduleResult | undefined,
): TechnicianScheduleView[] {
  if (!result) return [];
  return result.technicians.map((technician) => toView(result, technician));
}

function toView(
  result: ScheduleResult,
  technician: ScheduleResult["technicians"][number],
): TechnicianScheduleView {
  const appointmentsById = new Map(result.appointments.map((a) => [a.id, a]));
  const tasksById = new Map(result.tasks.map((t) => [t.id, t]));

  const appointments: Appointment[] = [];
  const scheduledTasks: TaskListItem[] = [];
  const breakOverrides: ScheduleEntry[] = [];
  const timeManagementEntries: ScheduleEntry[] = [];
  const absenceIds = new Set<number>();

  for (const entry of technician.entries) {
    switch (entry.type) {
      case "APPOINTMENT": {
        const owner = entry.appointmentId != null && appointmentsById.get(entry.appointmentId);
        if (owner) appointments.push(owner);
        break;
      }
      case "TASK": {
        const owner = entry.taskId != null && tasksById.get(entry.taskId);
        if (owner) scheduledTasks.push(owner);
        break;
      }
      case "ABSENCE":
        timeManagementEntries.push(entry);
        if (entry.absenceId != null) absenceIds.add(entry.absenceId);
        break;
      case "BREAK":
        breakOverrides.push(entry);
        break;
    }
  }

  return {
    technicianId: String(technician.technicianId),
    technicianName: technician.technicianName,
    technicianAvatar: technician.technicianAvatar,
    workingDays: technician.workingDays,
    shiftStart: technician.shiftStart,
    shiftEnd: technician.shiftEnd,
    defaultBreakStart: technician.defaultBreakStart,
    breakDurationMinutes: technician.breakDurationMinutes,
    appointments,
    breakOverrides,
    timeManagementEntries,
    scheduledTasks,
    absences: result.absences.filter((a) => absenceIds.has(a.id)),
  };
}
